/* eslint-disable react/prop-types */
/**
 * Iter 338 — 2026-02-25 · Gallery Reconcile card.
 *
 * Owner + admin only. Sits in Settings → Platform. Two buttons:
 *
 *   1. "Preview" (GET /api/admin/gallery/reconcile/preview)
 *      Cheap sha256 count-only aggregation. Tells you how many
 *      duplicate rows exist without touching Mongo.
 *
 *   2. "Run reconcile" (POST /api/admin/gallery/reconcile?dry_run=false)
 *      Rewrites every JC / ticket / customer / site / chat / gallery_items
 *      reference from duplicate id → canonical (earliest by created_at),
 *      then soft-deletes the duplicates. Idempotent — re-running is a
 *      no-op after the first successful pass.
 *
 * We ALWAYS show the dry-run first so the admin can eyeball counts
 * before committing.
 */
import { useState } from "react";
import { Images, Loader2, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function GalleryReconcileCard() {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applied, setApplied] = useState(null);

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/gallery/reconcile/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runDryRun = async () => {
    setDryRunLoading(true);
    try {
      const { data } = await api.post("/admin/gallery/reconcile?dry_run=true");
      setDryRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed.");
    } finally {
      setDryRunLoading(false);
    }
  };

  const runApply = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `About to collapse ${dryRun?.duplicates_to_collapse || "?"} duplicate uploads across `
      + `${dryRun?.groups_with_duplicates || "?"} sha256 groups.\n\n`
      + "Duplicates will be soft-deleted (recoverable from the Bin for 90 days). "
      + "All JC / ticket / chat / customer references will be rewritten to point at "
      + "the earliest canonical upload.\n\nContinue?"
    )) return;
    setApplyLoading(true);
    try {
      const { data } = await api.post("/admin/gallery/reconcile?dry_run=false");
      setApplied(data);
      toast.success(
        `Reconciled ${data.duplicates_to_collapse} duplicates · `
        + `${data.references_rewritten} refs rewritten.`
      );
      // Refresh the preview so the number resets to 0.
      runPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed.");
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="hv-card p-5" data-testid="gallery-reconcile-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center">
          <Images className="w-5 h-5 text-[#1FB6C1]" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-bold">Gallery reconcile</h2>
          <p className="text-xs text-[#a0aabf]">
            Collapse duplicate uploads (identical bytes stored multiple times) into a single canonical row.
            Every JC, ticket, chat, customer and site reference is rewritten to point at the earliest copy.
            Duplicates are soft-deleted and recoverable from the Bin for 90 days.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mt-4">
        <button
          type="button"
          onClick={runPreview}
          disabled={previewLoading}
          className="hv-btn hv-btn-ghost text-xs justify-center"
          data-testid="gallery-reconcile-preview-btn"
        >
          {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          1. Count duplicates
        </button>
        <button
          type="button"
          onClick={runDryRun}
          disabled={dryRunLoading || !preview || preview.duplicates_to_collapse === 0}
          className="hv-btn hv-btn-ghost text-xs justify-center"
          data-testid="gallery-reconcile-dryrun-btn"
        >
          {dryRunLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
          2. Dry-run
        </button>
        <button
          type="button"
          onClick={runApply}
          disabled={applyLoading || !dryRun || dryRun.duplicates_to_collapse === 0}
          className="hv-btn hv-btn-primary text-xs justify-center"
          data-testid="gallery-reconcile-apply-btn"
        >
          {applyLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          3. Apply
        </button>
      </div>

      {preview !== null && (
        <div className="mt-4 rounded border border-white/10 bg-white/[0.02] p-3 text-xs" data-testid="gallery-reconcile-preview">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-2">Preview</div>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Sha groups with dupes" value={preview.groups_with_duplicates} />
            <Stat label="Duplicate rows" value={preview.duplicates_to_collapse} accent={preview.duplicates_to_collapse ? "#F59E0B" : "#10B981"} />
          </div>
          {preview.duplicates_to_collapse === 0 && (
            <div className="mt-2 text-[10px] text-emerald-300">✓ Gallery is already clean — no duplicates found.</div>
          )}
        </div>
      )}

      {dryRun && (
        <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/[0.04] p-3 text-xs" data-testid="gallery-reconcile-dryrun-result">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-2">Dry-run — nothing was changed</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Stat label="Groups" value={dryRun.groups_with_duplicates} />
            <Stat label="Rows to collapse" value={dryRun.duplicates_to_collapse} accent="#F59E0B" />
          </div>
          {dryRun.sample_groups?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[10px] text-[#a0aabf] hover:text-white">
                Sample groups ({dryRun.sample_groups.length})
              </summary>
              <div className="mt-2 space-y-1 max-h-40 overflow-auto font-mono text-[10px]">
                {dryRun.sample_groups.map((g) => (
                  <div key={g.sha256_prefix} className="border-l-2 border-white/10 pl-2 py-1">
                    <div className="text-[#a0aabf]">sha:{g.sha256_prefix}… · {g.count} copies</div>
                    <div className="text-emerald-300">→ keep: {g.canonical.slice(0, 12)}…</div>
                    <div className="text-rose-300/80">
                      collapse: {g.duplicates.slice(0, 3).map(d => d.slice(0, 8)).join(", ")}
                      {g.duplicates.length > 3 ? ` +${g.duplicates.length - 3} more` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {applied && (
        <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-500/[0.04] p-3 text-xs" data-testid="gallery-reconcile-applied">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-2">Reconcile applied</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <Stat label="Duplicates collapsed" value={applied.duplicates_to_collapse} accent="#10B981" />
            <Stat label="References rewritten" value={applied.references_rewritten} accent="#10B981" />
          </div>
          {applied.references_by_field && Object.keys(applied.references_by_field).length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[10px] text-[#a0aabf] hover:text-white">
                By field
              </summary>
              <div className="mt-2 font-mono text-[10px] grid grid-cols-2 gap-x-3 gap-y-0.5">
                {Object.entries(applied.references_by_field)
                  .filter(([, v]) => v > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-white/5 py-0.5">
                      <span className="text-[#a0aabf] truncate">{k}</span>
                      <span className="text-emerald-300 font-bold">{v}</span>
                    </div>
                  ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "#dde3ec" }) {
  return (
    <div className="border border-white/10 rounded px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-lg font-bold font-display" style={{ color: accent }}>
        {typeof value === "number" ? value.toLocaleString() : value ?? "—"}
      </div>
    </div>
  );
}
